import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Calendar, CalendarDays, Clock, MapPin, Users, Video, ExternalLink, 
  Edit2, Trash2, X, Loader2, Send, Bell, Share2, ImagePlus
} from "lucide-react";
import { API, authFetch } from "@/App";

export function EventsDialog({ 
  open, 
  onOpenChange, 
  events = [],
  onRefresh
}) {
  const [eventForm, setEventForm] = useState({
    title: "", description: "", date: "", time: "", 
    duration: "60", location: "Online", meeting_link: "", 
    payment_link: "", flyer_image: "", price: 0, max_attendees: 15
  });
  const [editingEvent, setEditingEvent] = useState(null);
  const [savingEvent, setSavingEvent] = useState(false);
  const [selectedEventAttendees, setSelectedEventAttendees] = useState(null);
  const [reminderMessage, setReminderMessage] = useState("");
  const [sendingReminder, setSendingReminder] = useState(false);
  const [activeTab, setActiveTab] = useState("list");

  const resetForm = () => {
    setEventForm({
      title: "", description: "", date: "", time: "", 
      duration: "60", location: "Online", meeting_link: "", 
      payment_link: "", flyer_image: "", price: 0, max_attendees: 15
    });
    setEditingEvent(null);
    setSelectedEventAttendees(null);
    setActiveTab("list");
  };

  const handleCreateEvent = async () => {
    if (!eventForm.title || !eventForm.date || !eventForm.time) {
      toast.error("Title, date, and time are required");
      return;
    }
    setSavingEvent(true);
    try {
      const response = await authFetch(`${API}/consultant/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(eventForm)
      });
      if (response.ok) {
        toast.success("Event created!");
        resetForm();
        onRefresh?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create event");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;
    setSavingEvent(true);
    try {
      const response = await authFetch(`${API}/consultant/events/${editingEvent.workshop_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(eventForm)
      });
      if (response.ok) {
        toast.success("Event updated!");
        resetForm();
        onRefresh?.();
      }
    } catch (_error) {
      toast.error("Failed to update event");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteEvent = async (workshopId) => {
    if (!confirm("Delete this event?")) return;
    try {
      const response = await authFetch(`${API}/consultant/events/${workshopId}`, {
        method: "DELETE"
      });
      if (response.ok) {
        toast.success("Event deleted");
        onRefresh?.();
      }
    } catch (_error) {
      toast.error("Failed to delete event");
    }
  };

  const openEditEventDialog = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title || "",
      description: event.description || "",
      date: event.date || "",
      time: event.time || "",
      duration: event.duration || "60",
      location: event.location || "Online",
      meeting_link: event.meeting_link || "",
      payment_link: event.payment_link || "",
      flyer_image: event.flyer_image || "",
      price: event.price || 0,
      max_attendees: event.max_attendees || 15
    });
    setActiveTab("create"); // Switch to edit tab
  };

  const fetchEventAttendees = async (workshopId) => {
    try {
      const res = await authFetch(`${API}/consultant/events/${workshopId}/registrations`);
      if (res.ok) {
        const data = await res.json();
        setSelectedEventAttendees(data);
        setActiveTab("attendees"); // Switch to attendees tab
      }
    } catch (_error) {
      toast.error("Failed to fetch attendees");
    }
  };

  const handleSendReminder = async (workshopId) => {
    setSendingReminder(true);
    try {
      const response = await authFetch(`${API}/consultant/events/${workshopId}/send-reminder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: reminderMessage })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setReminderMessage("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send reminders");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSendingReminder(false);
    }
  };

  const handleRemoveAttendee = async (workshopId, userId) => {
    if (!confirm("Remove this attendee?")) return;
    try {
      const response = await authFetch(`${API}/consultant/events/${workshopId}/remove-attendee`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId })
      });
      if (response.ok) {
        toast.success("Attendee removed");
        fetchEventAttendees(workshopId);
        onRefresh?.();
      }
    } catch (_error) {
      toast.error("Failed to remove attendee");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      onOpenChange(isOpen);
      if (!isOpen) resetForm();
    }}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-blue-600" />
            Events Management
          </DialogTitle>
          <DialogDescription>
            Create, edit, and manage workshops and events
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="list">Events ({events.length})</TabsTrigger>
            <TabsTrigger value="create">{editingEvent ? "Edit Event" : "Create Event"}</TabsTrigger>
            <TabsTrigger value="attendees" disabled={!selectedEventAttendees}>
              Attendees {selectedEventAttendees && `(${selectedEventAttendees.count})`}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="list" className="flex-1 overflow-y-auto mt-4 space-y-3">
            {events.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <Calendar className="w-12 h-12 mx-auto mb-2 text-neutral-300" />
                <p>No events yet</p>
                <p className="text-xs mt-1">Create your first event</p>
              </div>
            ) : (
              events.map((event) => (
                <Card key={event.workshop_id} className={`border-l-4 ${
                  new Date(event.date) < new Date() ? "border-l-neutral-300 bg-neutral-50" : "border-l-blue-500"
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-semibold">{event.title}</h4>
                          {event.price > 0 && (
                            <Badge className="bg-green-100 text-green-700">${event.price}</Badge>
                          )}
                          {new Date(event.date) < new Date() && (
                            <Badge variant="outline" className="text-neutral-500">Past</Badge>
                          )}
                        </div>
                        <div className="flex items-center flex-wrap gap-3 text-sm text-neutral-600 mb-2">
                          <span className="flex items-center">
                            <CalendarDays className="w-4 h-4 mr-1" />
                            {event.date}
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {event.time}
                          </span>
                          <span className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {event.location}
                          </span>
                          <span className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            {event.registration_count || 0}/{event.max_attendees}
                          </span>
                        </div>
                        {event.description && (
                          <p className="text-sm text-neutral-500 line-clamp-2">{event.description}</p>
                        )}
                        {event.flyer_image && (
                          <img src={event.flyer_image} alt="Event flyer" className="mt-2 w-full max-h-40 object-cover rounded-lg border" />
                        )}
                      </div>
                      <div className="flex items-center space-x-1 ml-4">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => fetchEventAttendees(event.workshop_id)}
                          title="View Attendees"
                        >
                          <Users className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openEditEventDialog(event)}
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600"
                          onClick={() => handleDeleteEvent(event.workshop_id)}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 mt-3 pt-3 border-t">
                      {event.meeting_link && (
                        <a href={event.meeting_link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center">
                          <Video className="w-3 h-3 mr-1" />Meeting Link
                        </a>
                      )}
                      {event.payment_link && (
                        <a href={event.payment_link} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:underline flex items-center">
                          <ExternalLink className="w-3 h-3 mr-1" />Payment Link
                        </a>
                      )}
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}/event/${event.workshop_id}`;
                          navigator.clipboard.writeText(url);
                          toast.success("Public event link copied!");
                        }}
                        className="text-xs text-purple-600 hover:underline flex items-center ml-auto"
                      >
                        <Share2 className="w-3 h-3 mr-1" />Share Public Link
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="create" className="flex-1 overflow-y-auto mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Event Title *</Label>
                <Input
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  placeholder="Business Growth Workshop"
                />
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={eventForm.time}
                  onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={eventForm.duration}
                  onChange={(e) => setEventForm({ ...eventForm, duration: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={eventForm.location}
                  onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                  placeholder="Online or Address"
                />
              </div>
              <div className="space-y-2">
                <Label>Price ($)</Label>
                <Input
                  type="number"
                  value={eventForm.price}
                  onChange={(e) => setEventForm({ ...eventForm, price: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Attendees</Label>
                <Input
                  type="number"
                  value={eventForm.max_attendees}
                  onChange={(e) => setEventForm({ ...eventForm, max_attendees: parseInt(e.target.value) || 15 })}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  placeholder="Event description..."
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Meeting Link (Zoom, etc.)</Label>
                <Input
                  value={eventForm.meeting_link}
                  onChange={(e) => setEventForm({ ...eventForm, meeting_link: e.target.value })}
                  placeholder="https://zoom.us/..."
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Link</Label>
                <Input
                  value={eventForm.payment_link}
                  onChange={(e) => setEventForm({ ...eventForm, payment_link: e.target.value })}
                  placeholder="https://stripe.com/..."
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Event Flyer / Photo</Label>
                {eventForm.flyer_image ? (
                  <div className="relative">
                    <img src={eventForm.flyer_image} alt="Flyer preview" className="w-full max-h-48 object-cover rounded-lg border" />
                    <Button size="sm" variant="destructive" className="absolute top-2 right-2 h-7 px-2" onClick={() => setEventForm({ ...eventForm, flyer_image: "" })}>
                      <X className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-neutral-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                    <ImagePlus className="w-6 h-6 text-neutral-400 mb-1" />
                    <span className="text-xs text-neutral-500">Click to upload flyer or photo (max 2MB)</span>
                    <span className="text-[10px] text-neutral-400 mt-0.5">Recommended: 1200 x 628px (landscape) or 1080 x 1080px (square)</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2MB"); return; }
                      const reader = new FileReader();
                      reader.onload = () => setEventForm({ ...eventForm, flyer_image: reader.result });
                      reader.readAsDataURL(file);
                    }} />
                  </label>
                )}
              </div>
            </div>
            <Button 
              onClick={editingEvent ? handleUpdateEvent : handleCreateEvent}
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={savingEvent}
            >
              {savingEvent ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              ) : editingEvent ? (
                "Update Event"
              ) : (
                "Create Event"
              )}
            </Button>
            {editingEvent && (
              <Button variant="outline" onClick={resetForm} className="w-full">
                Cancel Edit
              </Button>
            )}
          </TabsContent>

          <TabsContent value="attendees" className="flex-1 overflow-y-auto mt-4">
            {selectedEventAttendees && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">{selectedEventAttendees.workshop?.title}</h4>
                  <Badge>{selectedEventAttendees.count} registered</Badge>
                </div>
                
                <div className="space-y-2">
                  <Label>Send Reminder to All</Label>
                  <div className="flex space-x-2">
                    <Input
                      value={reminderMessage}
                      onChange={(e) => setReminderMessage(e.target.value)}
                      placeholder="Optional custom message..."
                    />
                    <Button
                      onClick={() => handleSendReminder(selectedEventAttendees.workshop?.workshop_id)}
                      disabled={sendingReminder}
                      className="bg-amber-500 hover:bg-amber-600"
                    >
                      {sendingReminder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {selectedEventAttendees.attendees?.length === 0 ? (
                  <p className="text-center py-4 text-neutral-500">No registrations yet</p>
                ) : (
                  <div className="space-y-2">
                    {selectedEventAttendees.attendees?.map((attendee) => (
                      <div key={attendee.user_id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                        <div>
                          <p className="font-medium">{attendee.name}</p>
                          <p className="text-sm text-neutral-500">{attendee.email}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline">{attendee.role}</Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleRemoveAttendee(selectedEventAttendees.workshop?.workshop_id, attendee.user_id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
